import React, { useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import UsuariosServiceAPI from '../../services/usuariosServiceAPI';

interface RedefinirSenhaModalProps {
  isOpen: boolean;
  onClose: () => void;
  usuarioId: string;
  usuarioNome: string;
  onSuccess: () => void;
}

const RedefinirSenhaModal: React.FC<RedefinirSenhaModalProps> = ({
  isOpen,
  onClose,
  usuarioId,
  usuarioNome,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    novaSenha: '',
    confirmarNovaSenha: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({
    novaSenha: '',
    confirmarNovaSenha: ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpa erro do campo quando usuário digita
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.novaSenha !== formData.confirmarNovaSenha) {
      alert('A nova senha e a confirmação não coincidem!');
      return;
    }

    // Validação de força de senha (mínimo 12 caracteres, maiúsculas, minúsculas, números e caracteres especiais)
    if (formData.novaSenha.length < 12) {
      alert('A nova senha deve ter pelo menos 12 caracteres!');
      return;
    }

    if (!/[A-Z]/.test(formData.novaSenha)) {
      alert('A nova senha deve conter pelo menos uma letra maiúscula!');
      return;
    }

    if (!/[a-z]/.test(formData.novaSenha)) {
      alert('A nova senha deve conter pelo menos uma letra minúscula!');
      return;
    }

    if (!/[0-9]/.test(formData.novaSenha)) {
      alert('A nova senha deve conter pelo menos um número!');
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.novaSenha)) {
      alert('A nova senha deve conter pelo menos um caractere especial (!@#$%^&*(),.?":{}|<>)!');
      return;
    }

    try {
      setLoading(true);
      await UsuariosServiceAPI.resetarSenha(usuarioId, formData.novaSenha);

      alert('Senha redefinida com sucesso! Você pode fazer login agora.');
      setFormData({ novaSenha: '', confirmarNovaSenha: '' });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      alert('Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Redefinir Senha - Primeiro Acesso" data-testid="redefinir-senha-modal">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center py-4">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-100 mb-4">
            <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.435-5.435A6 6 0 1121 9z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="welcome-message">
            Bem-vindo, {usuarioNome}!
          </h3>
          <p className="text-gray-600">
            Por segurança, você precisa redefinir sua senha no primeiro acesso.
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 p-4 border border-red-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nova Senha*
            </label>
            <Input
              type="password"
              value={formData.novaSenha}
              onChange={(value) => handleChange('novaSenha', value)}
              placeholder="Digite sua nova senha"
              error={errors.novaSenha}
              required
              dataTestId="nova-senha"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Nova Senha*
            </label>
            <Input
              type="password"
              value={formData.confirmarNovaSenha}
              onChange={(value) => handleChange('confirmarNovaSenha', value)}
              placeholder="Confirme sua nova senha"
              error={errors.confirmarNovaSenha}
              required
              dataTestId="confirmar-nova-senha"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-blue-600"
            loading={loading}
            disabled={loading}
            dataTestId="redefinir-senha"
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RedefinirSenhaModal;
